import { fetchJSONWithTimeout } from '@noths/polaris-client-utils';
import getConfig from 'next/config';

import { CATALOG_PRODUCTS_CACHE_TTL_SECONDS } from 'src/constants/catalogCache';
import { withRedisFetchCache } from 'src/services/withRedisFetchCache';
import { logger } from 'src/utils/serverLogger';
import type { CatalogServiceProductData } from './types';

const config = getConfig();
const { CATALOG_API_ENDPOINT, CATALOG_API_PATH } = config.publicRuntimeConfig;
const url = `${CATALOG_API_ENDPOINT}${CATALOG_API_PATH}`;
const WARNING_MESSAGE = 'failed to get batched product details data';

/**
 * Sorted and deduplicated so two sections asking for the same products share one
 * cache entry regardless of the order the codes appear in Contentstack. The
 * normalised list is also what gets sent upstream, so the cache key and the
 * request it stands for cannot drift apart. This order serves the cache and the
 * upstream request only, never the caller: `orderAsRequested` puts the caller's
 * own order back on the way out.
 */
const normalizeProductCodes = (productCodes: Array<number>) =>
  [...new Set(productCodes)].sort((a, b) => a - b);

/**
 * Contentstack order is merchandising intent, so product_carousel renders the
 * returned array positionally and needs it back in the order it asked for rather
 * than the sorted order above (or whatever order cat-service-api happens to
 * answer in, which was never guaranteed).
 *
 * Builds a new array: the array this receives is the one held in the cache and
 * shared with every caller coalesced onto the same in-flight request, so sorting
 * it in place would reorder their results too. Deduplicated in step with
 * `normalizeProductCodes`, and codes the catalog did not return are dropped,
 * which `getProductsValidationErrors` already reports in preview mode.
 */
const orderAsRequested = (
  products: CatalogServiceProductData[],
  productCodes: Array<number>,
): CatalogServiceProductData[] => {
  const productsByCode = new Map(products.map((product) => [product.code, product]));

  return [...new Set(productCodes)]
    .map((code) => productsByCode.get(code))
    .filter((product): product is CatalogServiceProductData => !!product);
};

/**
 * Throws rather than returning null on failure, which is what lets
 * `withRedisFetchCache` tell success from failure: it serves the previous
 * response when there is one and falls back to null when there is not. Returning
 * null here would cache the failure as if it were an empty catalog and blank
 * every affected carousel for the whole TTL.
 */
const fetchProductsFromCatalog = async (
  productCodes: Array<number>,
): Promise<CatalogServiceProductData[]> => {
  let response: Response;

  try {
    response = await fetchJSONWithTimeout(url, {
      method: 'POST',
      body: {
        productCodes,
      },
      timeout: 800,
    });
  } catch (error) {
    logger.warn(WARNING_MESSAGE, { error: `${String(error)}` });

    throw error;
  }

  if (!response.ok) {
    logger.warn(WARNING_MESSAGE, {
      error: `bad response code from cat-service-api: ${String(response.status)}`,
    });

    throw new Error(`bad response code from cat-service-api: ${String(response.status)}`);
  }

  const body = await response.json();

  if (!Array.isArray(body.products)) {
    logger.warn(WARNING_MESSAGE, { error: 'cat-service-api response had no products array' });

    throw new Error('cat-service-api response had no products array');
  }

  return body.products as CatalogServiceProductData[];
};

export const fetchProducts = async (
  productCodes: Array<number>,
): Promise<CatalogServiceProductData[] | null> => {
  const normalizedProductCodes = normalizeProductCodes(productCodes);

  const products = await withRedisFetchCache<CatalogServiceProductData[] | null>({
    allowCaching: true,
    cacheKey: `catalog-products-${normalizedProductCodes.join(',')}`,
    cacheTTLInSeconds: CATALOG_PRODUCTS_CACHE_TTL_SECONDS,
    fallbackOnError: null,
    fetcher: () => fetchProductsFromCatalog(normalizedProductCodes),
  });

  // null is a failed request, which callers surface as a preventRender error.
  return products && orderAsRequested(products, productCodes);
};
